/**
 * Maps front-office payment provider names to WalletAPI gateways.
 * Ported from legacy `SetDepositForm` / `SetWithdrawalForm` in financial.js.
 */

export type PaymentField =
  | 'amount'
  | 'bank'
  | 'iban'
  | 'accountHolder'
  | 'accountNumber'
  | 'cryptoAddress'
  | 'cryptocurrency'
  | 'network'
  | 'phone'
  | 'voucher'
  | 'walletType';

export type PaymentSuccessMode = 'redirect' | 'inline' | 'iframe' | 'popup' | 'notify';

export type PaymentBanksSource = 'none' | 'users' | 'wallet';

export interface PaymentProviderConfig {
  /** WalletAPI path segment, e.g. `ultrapay`. */
  walletSlug: string;
  /** Extra form fields merged into the deposit/withdrawal POST. */
  extras?: Record<string, string | number>;
  depositFields?: PaymentField[];
  withdrawalFields?: PaymentField[];
  success?: PaymentSuccessMode;
  banksSource?: PaymentBanksSource;
  /** Load WalletAPI wallet types (crypto). */
  walletTypes?: boolean;
  /** Prefer UsersAPI saved payment accounts on withdrawal. */
  paymentAccounts?: boolean;
}

export const ULTRAPAY_METHOD_IDS: Record<string, number> = {
  'ultrapay-papara': 1,
  'ultrapay-havale': 2,
  'ultrapay-crypto': 4,
  'ultrapay-fups': 8,
  'ultrapay-kredikarti': 10,
  'ultrapay-paycell': 15,
  'ultrapay-tosla': 20,
  'ultrapay-parakolay': 26,
  'ultrapay-moneytolia': 29,
  'ultrapay-ahlpay': 30,
  'ultrapay-hayhay': 33,
  'ultrapay-hadipay': 34,
  'ultrapay-h2h': 42,
  'ultrapay-joypayz': 44,
  'ultrapay-yesodwallet': 45,
};

export const ULTRAPAY_CRYPTO_PAIRS: { Cryptocurrency: string; Network: string }[] = [
  { Cryptocurrency: 'BTC', Network: 'BTC' },
  { Cryptocurrency: 'ETH', Network: 'ERC20' },
  { Cryptocurrency: 'LTC', Network: 'LTC' },
  { Cryptocurrency: 'TRX', Network: 'TRC20' },
  { Cryptocurrency: 'USDC', Network: 'SOL' },
  { Cryptocurrency: 'USDC', Network: 'ERC20' },
  { Cryptocurrency: 'USDT', Network: 'TRC20' },
  { Cryptocurrency: 'USDT', Network: 'ERC20' },
];

const DEFAULT_DEPOSIT_FIELDS: PaymentField[] = ['amount'];
const DEFAULT_WITHDRAWAL_FIELDS: PaymentField[] = ['bank', 'iban', 'accountHolder', 'amount'];

/** Exact-name configs (normalized lower-case provider name). */
const EXACT: Record<string, PaymentProviderConfig> = {
  nowpayment: {
    walletSlug: 'nowpayment',
    extras: { ProviderName: 'nowpayment' },
    depositFields: ['amount', 'walletType'],
    withdrawalFields: ['amount', 'walletType', 'cryptoAddress'],
    success: 'inline',
    walletTypes: true,
  },
  fixturka: {
    walletSlug: 'fixturka',
    extras: { ProviderName: 'Fixturka' },
    success: 'redirect',
  },
  redeposit: {
    walletSlug: 'redeposit',
    extras: { ProviderName: 'ReDeposit' },
    depositFields: ['amount'],
    // WalletAPI RedepositDepositResponse is Success/Error only (no redirect URL).
    success: 'notify',
  },
  payco: {
    walletSlug: 'payco',
    extras: { ProviderName: 'Payco' },
    success: 'redirect',
  },
  telebirr: {
    walletSlug: 'telebirr',
    extras: { ProviderName: 'Telebirr' },
    depositFields: ['amount', 'phone'],
    withdrawalFields: ['amount', 'phone'],
    success: 'notify',
  },
  ampay: {
    walletSlug: 'ampay',
    extras: {
      ProviderName: 'AmPay',
      PaymentMethod: 'BANK_TRANSFER',
      SubMethod: 'IBAN',
    },
    depositFields: ['amount'],
    withdrawalFields: ['iban', 'accountHolder', 'amount'],
    success: 'redirect',
  },
  'ampay-humo': {
    walletSlug: 'ampay',
    extras: {
      ProviderName: 'AmPay-HUMO',
      PaymentMethod: 'P2P_CIS',
      SubMethod: 'CARD',
      Bank: 'HUMO',
    },
    success: 'inline',
  },
  'ampay-uzcard': {
    walletSlug: 'ampay',
    extras: {
      ProviderName: 'AmPay-UZCARD',
      PaymentMethod: 'P2P_CIS',
      SubMethod: 'CARD',
      Bank: 'UZCARD',
    },
    success: 'inline',
  },
  'maksipara-papara': { walletSlug: 'maksipara', extras: { ProviderName: 'Papara' }, success: 'redirect' },
  'maksipara-banktransfer': {
    walletSlug: 'maksipara',
    extras: { ProviderName: 'BankTransfer' },
    success: 'redirect',
    banksSource: 'wallet',
  },
  'maksipara-payfix': { walletSlug: 'maksipara', extras: { ProviderName: 'Payfix' }, success: 'redirect' },
  'maksipara-crypto': {
    walletSlug: 'maksipara',
    extras: { ProviderName: 'Crypto' },
    depositFields: ['amount', 'walletType'],
    withdrawalFields: ['amount', 'walletType', 'cryptoAddress'],
    success: 'redirect',
    walletTypes: true,
  },
  'maksipara-creditcard': { walletSlug: 'maksipara', extras: { ProviderName: 'CreditCard' }, success: 'redirect' },
  'maksipara-mefete': { walletSlug: 'maksipara', extras: { ProviderName: 'Mefete' }, success: 'redirect' },
  'maksipara-pep': { walletSlug: 'maksipara', extras: { ProviderName: 'Pep' }, success: 'redirect' },
  'maksipara-hayhay': { walletSlug: 'maksipara', extras: { ProviderName: 'Hayhay' }, success: 'redirect' },
  'maksipara-paycell': { walletSlug: 'maksipara', extras: { ProviderName: 'Paycell' }, success: 'redirect' },
  'maksipara-paybol': { walletSlug: 'maksipara', extras: { ProviderName: 'Paybol' }, success: 'redirect' },
  'maksipara-popypara': { walletSlug: 'maksipara', extras: { ProviderName: 'Popypara' }, success: 'redirect' },
  'wexapay-papara': { walletSlug: 'wexapay', extras: { ProviderName: 'Papara' }, success: 'redirect' },
  'wexapay-havale': {
    walletSlug: 'wexapay',
    extras: { ProviderName: 'BankTransfer' },
    success: 'redirect',
    banksSource: 'wallet',
  },
  'wexapay-payfix': { walletSlug: 'wexapay', extras: { ProviderName: 'Payfix' }, success: 'redirect' },
  'wexapay-pep': { walletSlug: 'wexapay', extras: { ProviderName: 'Pep' }, success: 'redirect' },
  'wexapay-mefete': { walletSlug: 'wexapay', extras: { ProviderName: 'Mefete' }, success: 'redirect' },
  'heetpay-havale': {
    walletSlug: 'heetpay',
    extras: { ProviderName: 'Havale' },
    success: 'redirect',
    banksSource: 'wallet',
  },
  'heetpay-papara': { walletSlug: 'heetpay', extras: { ProviderName: 'Papara' }, success: 'redirect' },
  'contrapay-havale': {
    walletSlug: 'contrapay',
    depositFields: ['amount', 'bank'],
    withdrawalFields: ['bank', 'iban', 'accountHolder', 'amount'],
    success: 'inline',
    banksSource: 'wallet',
  },
  'contrapay-humo': {
    walletSlug: 'contrapay',
    depositFields: ['amount', 'bank'],
    success: 'inline',
    banksSource: 'wallet',
  },
  'contrapay-uzcard': {
    walletSlug: 'contrapay',
    depositFields: ['amount', 'bank'],
    success: 'inline',
    banksSource: 'wallet',
  },
  'contrapay-papara': {
    walletSlug: 'contrapay',
    extras: { ProviderName: 'Papara' },
    success: 'inline',
  },
  'aninda-banka': { walletSlug: 'aninda', extras: { ProviderName: 'AnindaBanka' }, success: 'redirect' },
  'aninda-havale': {
    walletSlug: 'aninda',
    extras: { ProviderName: 'AnindaHavale' },
    success: 'redirect',
    banksSource: 'wallet',
  },
  'aninda-paypay': { walletSlug: 'aninda', extras: { ProviderName: 'AnindaPayPay' }, success: 'redirect' },
  'aninda-parazula': { walletSlug: 'aninda', extras: { ProviderName: 'AnindaParazula' }, success: 'redirect' },
  'aninda-kripto': { walletSlug: 'aninda', extras: { ProviderName: 'AnindaKripto' }, success: 'redirect' },
  'aninda-kredikarti': {
    walletSlug: 'aninda',
    extras: { ProviderName: 'AnindaKrediKarti' },
    success: 'redirect',
  },
  'aninda-papara': { walletSlug: 'aninda', extras: { ProviderName: 'AnindaPapara' }, success: 'redirect' },
  'justpay-bank-deposit': {
    walletSlug: 'justpay',
    extras: { ProviderName: 'JustPayDeposit' },
    success: 'redirect',
  },
  'hemenpay-havale': {
    walletSlug: 'hemenpay',
    extras: { ProviderName: 'HemenPay-Havale' },
    success: 'redirect',
    banksSource: 'wallet',
  },
  'hemenpay-paypay': {
    walletSlug: 'hemenpay',
    extras: { ProviderName: 'HemenPay-PayPay' },
    success: 'redirect',
  },
  'hemenpay-papara': {
    walletSlug: 'hemenpay',
    extras: { ProviderName: 'HemenPay-Papara' },
    success: 'redirect',
  },
  'hemenpay-parolapara': {
    walletSlug: 'hemenpay',
    extras: { ProviderName: 'HemenPay-Parolapara' },
    success: 'redirect',
  },
  'paytopayz-havale': {
    walletSlug: 'paytopayz',
    extras: { ProviderName: 'PayToPayz-Havale', PaymentMethod: 'HAVALE' },
    success: 'inline',
  },
  'paytopayz-kredikarti': {
    walletSlug: 'paytopayz',
    extras: { ProviderName: 'PayToPayz-Kredikarti', PaymentMethod: 'KREDIKARTI' },
    success: 'redirect',
  },
  'paytopayz-crypto': {
    walletSlug: 'paytopayz',
    extras: { ProviderName: 'PayToPayz-Crypto', PaymentMethod: 'CRYPTO' },
    success: 'inline',
  },
  'paytopayz-dinamikpay': {
    walletSlug: 'paytopayz',
    extras: { ProviderName: 'PayToPayz-Dinamikpay', PaymentMethod: 'DINAMIKPAY' },
    success: 'redirect',
  },
  'paytopayz-papara': {
    walletSlug: 'paytopayz',
    extras: { ProviderName: 'PayToPayz-Papara', PaymentMethod: 'PAPARA' },
    success: 'redirect',
  },
  paystack: {
    walletSlug: 'paystack',
    success: 'iframe',
    paymentAccounts: true,
  },
  kriptopay: {
    walletSlug: 'kriptopay',
    depositFields: ['amount', 'cryptocurrency', 'network'],
    withdrawalFields: ['amount', 'cryptocurrency', 'network', 'cryptoAddress'],
    success: 'popup',
  },
  neosurf: { walletSlug: 'neosurf', success: 'iframe' },
  parsigram: { walletSlug: 'parsigram', success: 'iframe' },
  twiga: { walletSlug: 'twiga', depositFields: ['voucher'], success: 'notify' },
  flutterwave: { walletSlug: 'flutterwave', success: 'popup' },
  manualpay: {
    walletSlug: 'manualpay',
    depositFields: ['amount', 'bank'],
    withdrawalFields: DEFAULT_WITHDRAWAL_FIELDS,
    success: 'inline',
    banksSource: 'wallet',
  },
  safepay: {
    walletSlug: 'safepay',
    success: 'redirect',
    banksSource: 'wallet',
  },
};

/** Normalize provider display name to a lookup key (legacy uses lower-case name). */
export function normalizeProviderName(name: string | null | undefined): string {
  return (name ?? '').trim().toLowerCase().replace(/\s+/g, '-');
}

/** Brand-site folder for payment method icons (legacy SportsbookApp assets). */
export const PAYMENT_PROVIDER_LOGO_PATH = '/assets/images/providers/svg';

/**
 * Asset slug for `{PAYMENT_PROVIDER_LOGO_PATH}/{slug}.svg`.
 * Matches legacy: `Name.replace(/\W/g, '').toLowerCase()`.
 */
export function paymentProviderSlug(name: string | null | undefined): string {
  return (name ?? '').replace(/\W/g, '').toLowerCase();
}

/**
 * Absolute URL for a payment provider logo on the brand origin
 * (e.g. `https://contra11.com/assets/images/providers/svg/papara.svg`).
 *
 * Resolution order:
 * 1. Absolute `logoFromApi` URL as-is
 * 2. API logo path/filename under the brand providers folder
 * 3. Slug derived from the provider display name (legacy mapping)
 */
export function paymentProviderLogoUrl(
  brandBaseUrl: string,
  providerName: string | null | undefined,
  logoFromApi?: string | null,
): string {
  const base = brandBaseUrl.replace(/\/+$/, '');
  const apiLogo = (logoFromApi ?? '').trim();

  if (apiLogo) {
    if (/^https?:\/\//i.test(apiLogo)) return apiLogo;

    // `/assets/images/providers/svg/foo.svg` or `foo.svg` / `foo`
    const normalized = apiLogo.replace(/^\/+/, '');
    if (normalized.startsWith('assets/images/providers/svg/')) {
      return `${base}/${normalized}`;
    }
    const fileName = normalized.replace(/^.*\//, '');
    const withExt = fileName.toLowerCase().endsWith('.svg') ? fileName : `${fileName}.svg`;
    return `${base}${PAYMENT_PROVIDER_LOGO_PATH}/${withExt}`;
  }

  return `${base}${PAYMENT_PROVIDER_LOGO_PATH}/${paymentProviderSlug(providerName)}.svg`;
}

function withDefaults(config: PaymentProviderConfig): PaymentProviderConfig {
  return {
    depositFields: DEFAULT_DEPOSIT_FIELDS,
    withdrawalFields: DEFAULT_WITHDRAWAL_FIELDS,
    success: 'notify',
    banksSource: 'none',
    ...config,
    extras: config.extras ? { ...config.extras } : undefined,
  };
}

/** True when the UsersAPI marks the provider as Redeposit (or the name is Redeposit). */
export function isRedepositProvider(
  provider: { Name?: string | null; IsRedeposit?: boolean } | null | undefined,
): boolean {
  if (!provider) return false;
  if (provider.IsRedeposit) return true;
  return normalizeProviderName(provider.Name) === 'redeposit';
}

/**
 * Resolve WalletAPI routing + form shape for a front-office provider name.
 * Unmapped names fall back to sanitized name as wallet slug.
 * Redeposit providers always route to `api/payments/redeposit/deposit`.
 */
export function resolvePaymentProvider(
  providerName: string | null | undefined,
  options?: { isRedeposit?: boolean },
): PaymentProviderConfig {
  const key = normalizeProviderName(providerName);

  if (options?.isRedeposit || key === 'redeposit') {
    return withDefaults({
      walletSlug: 'redeposit',
      extras: { ProviderName: 'ReDeposit' },
      depositFields: ['amount'],
      success: 'notify',
    });
  }

  if (key.startsWith('ultrapay')) {
    const methodId = ULTRAPAY_METHOD_IDS[key];
    const isCrypto = key === 'ultrapay-crypto';
    const isH2h = key === 'ultrapay-h2h';
    return withDefaults({
      walletSlug: 'ultrapay',
      extras: methodId != null ? { MethodId: methodId } : undefined,
      depositFields: isCrypto
        ? ['amount', 'cryptocurrency', 'network']
        : DEFAULT_DEPOSIT_FIELDS,
      withdrawalFields: isCrypto
        ? ['amount', 'cryptocurrency', 'network', 'cryptoAddress']
        : key.includes('havale') || isH2h
          ? DEFAULT_WITHDRAWAL_FIELDS
          : ['accountNumber', 'amount'],
      success: isH2h ? 'inline' : 'redirect',
      banksSource: key.includes('havale') || isH2h ? 'wallet' : 'none',
    });
  }

  const exact = EXACT[key];
  if (exact) return withDefaults(exact);

  // Prefix fallbacks for hyphenated families not listed exactly.
  if (key.startsWith('maksipara')) {
    return withDefaults({ walletSlug: 'maksipara', success: 'redirect' });
  }
  if (key.startsWith('wexapay')) {
    return withDefaults({ walletSlug: 'wexapay', success: 'redirect' });
  }
  if (key.startsWith('aninda')) {
    return withDefaults({ walletSlug: 'aninda', success: 'redirect' });
  }
  if (key.startsWith('hemenpay')) {
    return withDefaults({ walletSlug: 'hemenpay', success: 'redirect' });
  }
  if (key.startsWith('paytopayz')) {
    return withDefaults({ walletSlug: 'paytopayz', success: 'redirect' });
  }
  if (key.startsWith('contrapay')) {
    return withDefaults({ walletSlug: 'contrapay', success: 'inline', banksSource: 'wallet' });
  }
  if (key.startsWith('ampay')) {
    return withDefaults({ walletSlug: 'ampay', success: 'redirect' });
  }
  if (key.startsWith('heetpay')) {
    return withDefaults({ walletSlug: 'heetpay', success: 'redirect' });
  }

  const slug = paymentProviderSlug(providerName) || 'unknown';
  return withDefaults({ walletSlug: slug, success: 'notify' });
}

export function paymentFieldSet(
  config: PaymentProviderConfig,
  mode: 'deposit' | 'withdrawal',
): PaymentField[] {
  return mode === 'deposit'
    ? (config.depositFields ?? DEFAULT_DEPOSIT_FIELDS)
    : (config.withdrawalFields ?? DEFAULT_WITHDRAWAL_FIELDS);
}
